import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  MessageCircle,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Video
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { MessageBubble } from '../components/dm/MessageBubble';
import { MessageInput } from '../components/dm/MessageInput';
import { useLiveVideo } from '../context/LiveVideoContext';

const LOGO_URL = '/logo.webp';
const API = process.env.REACT_APP_BACKEND_URL;

// ============ CONVERSATIONS LIST ============

export const DirectMessagesPage = () => {
  const { api } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const res = await api().get('/direct-messages/conversations');
        setConversations(res.data.conversations || []);
      } catch (err) {
        console.error('Failed to load conversations:', err.message);
      }
      setLoading(false);
    };
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="direct-messages-page">
      <main className="container mx-auto px-6 py-6 max-w-2xl">
        <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-wider mb-6">Messages</h1>
        {conversations.length > 0 ? (
          <div className="space-y-3">
            {conversations.map((conv, idx) => (
              <Link key={conv.user.id} to={`/dm/${conv.user.id}`}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4 hover:border-primary/50 transition-colors"
                >
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={getImageUrl(conv.user?.profile_picture)} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      {conv.user.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium truncate">{conv.user.name}</span>
                      <span className="text-muted-foreground text-xs">{formatTime(conv.last_message.created_at)}</span>
                    </div>
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-white font-medium' : 'text-muted-foreground'}`}>
                      {conv.last_message.is_from_me && <span className="text-muted-foreground mr-1">You:</span>}
                      {conv.last_message.content}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.unread_count > 0 && (
                      <Badge className="bg-primary text-black min-w-[24px] justify-center">{conv.unread_count}</Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl p-12 text-center">
            <MessageCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-lg text-white mb-2">No Messages Yet</h3>
            <p className="text-muted-foreground mb-4">Start a conversation by visiting someone's profile</p>
            <Link to="/social"><Button className="bg-primary text-black">Find People</Button></Link>
          </div>
        )}
      </main>
    </div>
  );
};


// ============ CONVERSATION PAGE ============

export const ConversationPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [isBuddy, setIsBuddy] = useState(false);
  const [callLaunching, setCallLaunching] = useState(false);
  const live = useLiveVideo();
  const [canSend, setCanSend] = useState(true);
  const [cannotSendReason, setCannotSendReason] = useState('');
  const messagesEndRef = useRef(null);
  const playingAudioIdRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const audioRefs = useRef({});

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await api().get(`/direct-messages/with/${userId}`);
      setMessages(res.data.messages || []);
      setOtherUser(res.data.user);
      setCanSend(res.data.can_send);
      setCannotSendReason(res.data.cannot_send_reason || '');
      setIsBuddy(!!res.data.is_buddy);
    } catch (e) {
      console.debug('Failed to fetch messages:', e.message);
    }
    setLoading(false);
  };

  const toggleAudioPlayback = (msgId, mediaUrl) => {
    if (playingAudioId === msgId) {
      audioRefs.current[msgId]?.pause();
      setPlayingAudioId(null);
    } else {
      if (playingAudioId && audioRefs.current[playingAudioId]) audioRefs.current[playingAudioId].pause();
      if (!audioRefs.current[msgId]) {
        audioRefs.current[msgId] = new Audio(`${API}${mediaUrl}`);
        audioRefs.current[msgId].onended = () => setPlayingAudioId(null);
      }
      audioRefs.current[msgId].play();
      setPlayingAudioId(msgId);
    }
  };

  const handleEdit = async (messageId, content) => {
    try {
      const res = await api().put(`/direct-messages/${messageId}`, { content });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content: res.data.dm.content, is_edited: true } : m));
      toast.success('Message edited');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to edit message');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await api().delete(`/direct-messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message unsent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      const res = await api().post(`/direct-messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add reaction');
    }
  };

  const handleRemoveReaction = async (messageId) => {
    try {
      const res = await api().delete(`/direct-messages/${messageId}/react`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove reaction');
    }
  };

  const handleStartVideoCall = async () => {
    if (callLaunching || !isBuddy) return;
    if (live.session) {
      toast.info('You are already in a live session');
      return;
    }
    setCallLaunching(true);
    try {
      // Reuse the existing buddy live-invite endpoint — it creates a session,
      // verifies buddy status server-side (defense-in-depth), and notifies the
      // other party via the bell. We then start the LiveKit session ourselves
      // so the caller's PiP is up immediately while the buddy sees a "Join Live"
      // notification and accepts.
      const res = await api().post('/buddies/live-invite', { buddy_id: userId });
      const sessionId = res.data?.session_id;
      if (!sessionId) throw new Error('No session id returned');
      const launched = await live.startSession(sessionId);
      if (launched?.ok) {
        toast.success(`Calling ${otherUser?.name || 'your buddy'}…`, { icon: '📹' });
      } else {
        toast.error(launched?.error || 'Could not start the call');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the call');
    }
    setCallLaunching(false);
  };

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="conversation-page">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/messages')} className="text-white hover:text-primary transition-colors" data-testid="dm-back-to-inbox">
            <ArrowLeft className="h-5 w-5" />
          </button>
          {otherUser && (
            <Link to={`/user/${userId}`} className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity">
              <Avatar className="h-10 w-10">
                <AvatarImage src={getImageUrl(otherUser.profile_picture)} />
                <AvatarFallback className="bg-primary/20 text-primary">{otherUser.name?.charAt(0) || 'U'}</AvatarFallback>
              </Avatar>
              <span className="font-heading text-white">{otherUser.name}</span>
            </Link>
          )}
          {isBuddy && otherUser && (
            <Button
              size="icon"
              variant="ghost"
              onClick={handleStartVideoCall}
              disabled={callLaunching || !!live.session}
              className="text-primary hover:text-primary hover:bg-primary/10"
              title={live.session ? 'Already in a live session' : `Start a video call with ${otherUser.name}`}
              aria-label="start video call"
              data-testid="dm-video-call-btn"
            >
              {callLaunching
                ? <Loader2 className="h-5 w-5 animate-spin" />
                : <Video className="h-5 w-5" />}
            </Button>
          )}
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-8 w-8" />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-primary animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20">
            <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="flex items-center justify-center my-4">
                <span className="bg-card px-4 py-1 text-muted-foreground text-xs uppercase">{formatDate(dateMessages[0].created_at)}</span>
              </div>
              {dateMessages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isFromMe={msg.sender_id === currentUser?.id}
                  currentUserId={currentUser?.id}
                  currentUserName={currentUser?.name}
                  otherUser={otherUser}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAddReaction={handleAddReaction}
                  onRemoveReaction={handleRemoveReaction}
                  toggleAudioPlayback={toggleAudioPlayback}
                  playingAudioId={playingAudioId}
                  formatTime={formatTime}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        api={api}
        userId={userId}
        canSend={canSend}
        cannotSendReason={cannotSendReason}
        messages={messages}
        setMessages={setMessages}
      />
    </div>
  );
};

export default DirectMessagesPage;
